// src/lib/copy.ts
/**
 * Canonical user-facing strings for the admin surfaces.
 *
 * Voice rules (visual spec §25.1):
 * - Direct, professional, kind.
 * - No exclamation marks.
 * - Active voice.
 * - Verb-noun buttons.
 * - Resource names match backend labels.
 * - Sentence case except label-md tokens.
 */

export const adminCopy = {
  empty: {
    noData: {
      title: 'No {resource} yet',
      description: 'Create the first {resource} to get started.',
      cta: 'Create {resource}',
    },
    noResults: {
      title: 'No results',
      description: 'Try adjusting or clearing your filters.',
      cta: 'Clear filters',
    },
    hub: {
      title: "Your role doesn't include any workspaces yet.",
      description: 'Your administrator can grant you access.',
    },
  },

  error: {
    generic: "Couldn't load {resource}. Please try again.",
    forbidden: "You don't have permission to view this. Contact your administrator.",
    notFoundTitle: 'Not found',
    notFoundDescription: "This {resource} may have been removed or you don't have access.",
    notFoundCta: 'Back to {parent}',
    network: 'You appear to be offline. Check your connection and retry.',
    shapeMismatch: 'Something unexpected came back from the server. Our team has been notified.',
  },

  success: {
    create: '{Resource} created.',
    update: 'Changes saved.',
    delete: '{Resource} deleted.',
    invite: 'Invitation sent to {email}.',
  },

  confirm: {
    delete: {
      title: 'Delete {resource}?',
      description: 'This action cannot be undone.',
      confirm: 'Delete',
    },
    bulkDelete: {
      title: 'Delete {n} {resources}?',
      description: 'This action cannot be undone.',
      confirm: 'Delete {n}',
    },
    discard: {
      title: 'Discard unsaved changes?',
      description: 'Your changes will be lost.',
      confirm: 'Discard',
    },
  },

  phi: {
    tooltip: "Protected health information. Your role doesn't include access to this field.",
    sectionAllRedacted: 'All fields in this section require client:read_phi.',
  },

  viewport: {
    unsupportedTitle: 'This admin panel needs more room.',
    unsupportedDescription: 'Please use a device with a screen width of 1024 pixels or larger.',
  },

  shell: {
    helpLabel: 'Help',
    surfacePillPlatform: 'PLATFORM',
    surfacePillWorkspacePrefix: 'WORKSPACE',
    surfacePillClinical: 'CLINICIAN',
  },

  practitioners: {
    resource: 'practitioner',
    listTitle: 'Practitioners',
    listDescription: {
      platform: 'Every practitioner across the network. Read-only oversight view.',
      workspace: 'Manage your team of practitioners — profiles, locations, and availability.',
    },
    inviteHint: 'Practitioners are added by inviting a user with a clinical role.',
    inviteHintCta: 'Invite a practitioner',
    leadStatus: {
      // Two columns: "Lead" (this psy IS a lead at one or more projects) and
      // "Needs lead" (one or more of this psy's projects has no lead in the
      // routing chain). Em-dash in either column means "no signal" in that
      // dimension — chief, volunteer, no-assignments, and fully-covered psy
      // all render an em-dash with a tooltip explaining the specific reason.
      leadColumnHeader: 'Lead',
      needsLeadColumnHeader: 'Needs lead',
      // Em-dash tooltips — disambiguate the empty cell so admins know why.
      chiefTooltip: 'Chiefs are the routing fallback above any lead.',
      volunteerLeadTooltip: 'Volunteers are not eligible to lead.',
      noAssignmentsTooltip: 'No assignments in this scope.',
      notLeadTooltip: 'Not a lead at any project in this scope.',
      coveredTooltip: 'All projects route to a lead — no action needed.',
      // Lead chip label variants.
      leadAtAll: 'Lead at all',
      leadAtSingleProject: (project: string) => `${project} lead`,
      leadAtBoth: 'Both lead',
      leadPartial: (self: number, total: number) => `Lead · ${String(self)} of ${String(total)}`,
      // Needs-lead chip label variants.
      needsLead: 'Needs lead',
      needsLeadCount: (count: number) => `Needs lead · ${String(count)}`,
      // Tooltip breakdown (shared by both cells).
      tooltipHeading: 'Project lead status',
      tooltipLeadSelf: 'Lead (this psy)',
      tooltipLeadAssigned: 'Lead assigned',
      tooltipNeedsLead: 'Needs lead',
    },
    detail: {
      tabProfile: 'Profile',
      tabAssignments: 'Locations',
      /** Time-block CRUD tab. Set by the psychologist; admins can also
       *  edit it now (BE allows it), so the label is shared across
       *  surfaces. */
      tabAvailability: 'Availability',
      /** Admin-only tab for assigning a project to a slot the psy has
       *  marked as available. Distinct from `tabAvailability`: that one
       *  is when the psy is free; this one is what they're working on
       *  in that window. */
      tabSchedule: 'Schedule',
      editProfileCta: 'Edit profile',
    },
    profile: {
      sectionAbout: 'About',
      sectionExpertise: 'Expertise',
      sectionLanguages: 'Languages',
      sectionContact: 'Contact',
      bioLabel: 'Bio',
      genderLabel: 'Gender',
      yearsLabel: 'Years of experience',
      regNumberLabel: 'License number',
      specializationsLabel: 'Specializations',
      concernsLabel: 'Concerns',
      languagesLabel: 'Languages spoken',
      qualificationsLabel: 'Qualifications',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      updatedToast: 'Profile updated.',
    },
    assignments: {
      addCta: 'Add location',
      editCta: 'Edit',
      emptyTitle: 'No locations yet',
      emptyDescription: 'Assign this practitioner to one or more locations.',
      addedToast: 'Location added.',
      updatedToast: 'Location updated.',
      removedToast: 'Location removed.',
      activate: 'Activate',
      deactivate: 'Deactivate',
      remove: 'Remove',
      confirmRemoveTitle: 'Remove location?',
      confirmRemoveDescription: 'The practitioner will no longer be available at this location.',
      confirmRemoveConfirm: 'Remove',
      locationsCount: (n: number) => `${String(n)} location${n === 1 ? '' : 's'}`,
      // Add / Edit dialogs (a690359-aligned).
      addDialogTitle: 'Assign to a location',
      editDialogTitle: 'Edit assignment',
      orgLabel: 'Organization',
      orgPlaceholder: 'Select organization',
      locationLabel: 'Location',
      locationPlaceholder: 'Select location',
      locationOrgFirstHint: 'Pick an organization first.',
      projectsLabel: 'Projects',
      projectsHint: (role: string | null | undefined) => {
        if (role === 'chief_psychologist') {
          return 'Chiefs cover any project they’re assigned to. Case reports route here as the fallback when no lead supervisor is set.';
        }
        if (role === 'volunteer') {
          return 'Volunteers cover one project at one location. The named supervisor reviews their case reports.';
        }
        return 'Each project routes its own case reports. Leads review themselves; everyone else reports to the named supervisor, or to chief if none is set.';
      },
      supervisorLabel: 'Reports to',
      supervisorHint: (project: string) => `Reviews their ${project} reports.`,
      supervisorEmptyNoLeads: (project: string, location: string | null) =>
        location
          ? `No leads set for ${project} at ${location} yet. Reports route to chief until one is assigned.`
          : `No leads set for ${project} at this location yet. Reports route to chief until one is assigned.`,
      supervisorEmptySelfOnly: (firstName: string, project: string) =>
        `${firstName} is the only lead for ${project} here. Keep them a lead, or invite another lead psychologist.`,
      supervisorLeadAttestation: 'Leads review their own case reports — no supervisor needed.',
      supervisorPopoverFooter: (count: number, location: string | null) =>
        location
          ? `${String(count)} ${count === 1 ? 'lead' : 'leads'} at ${location}`
          : `${String(count)} ${count === 1 ? 'lead' : 'leads'} at this location`,
      submitAdd: 'Assign',
      submitSave: 'Save changes',
      cancel: 'Cancel',
    },
    availability: {
      templateTitle: 'Weekly working hours',
      templateEmpty: 'No recurring hours set yet. Edit to define a weekly rhythm.',
      editTemplateCta: 'Edit weekly hours',
      templateLastChanged: (when: string) => `Last updated ${when}`,
      rangeTitle: 'Schedule',
      emptyRangeTitle: 'No days in this range',
      emptyRangeDescription: 'Pick a wider date window to see scheduled working hours.',
      dayWorking: 'Working',
      dayLeave: 'On leave',
      sourceTemplate: 'Template',
      sourceOverride: 'Override',
      overrideDayCta: 'Edit this day',
      resetAction: 'Reset to template',
      overrideSavedToast: 'Day updated.',
      templateSavedToast: 'Weekly hours saved.',
      weeklyEditor: {
        title: 'Edit weekly working hours',
        description: 'Set a recurring pattern. Times must land on 15-minute boundaries.',
        save: 'Save schedule',
        cancel: 'Cancel',
        addBlock: 'Add block',
        copyFromMonday: 'Copy Mon to Tue–Fri',
        off: 'Off',
        emptyDayHint: 'No hours — day is off.',
      },
      overrideEditor: {
        description: 'Only this date is affected. Weekly pattern stays intact.',
        modeWorking: 'Working hours',
        modeLeave: 'On leave',
        reasonLabel: 'Reason (internal note)',
        reasonPlaceholder: 'e.g. Vacation, conference, personal day',
        save: 'Save override',
        cancel: 'Cancel',
        addBlock: 'Add block',
      },
    },
    statusMap: {
      activeLabel: 'Active',
      disabledLabel: 'Disabled',
      shadowLabel: 'Shadow',
      // "Invited" mirrors the Users-table convention (users/types/statuses.ts)
      // — same BE state, same chip, same word. Reads cleaner than the
      // longer "Pending invite" in dense table rows.
      pendingInviteLabel: 'Invited',
      pendingVerificationLabel: 'Pending verification',
    },
  },

  clients: {
    resource: 'client',
    listTitle: 'Clients',
    listDescription: {
      platform: 'Every client across the network. Read-only oversight view.',
      workspace: 'Manage the people you serve — profiles, sensitive info, and history.',
    },
    inviteHint: 'New clients are added by inviting a user with the client role.',
    detail: {
      tabProfile: 'Profile',
      tabRedFlags: 'Red flags',
      tabAssessments: 'Assessments',
      editCta: 'Edit profile',
    },
    profile: {
      sectionBasic: 'Basic information',
      sectionStudent: 'Student details',
      sectionCorporate: 'Corporate details',
      sectionOther: 'Other fields',
      sectionSensitive: 'Sensitive information',
      firstNameLabel: 'First name',
      lastNameLabel: 'Last name',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      statusLabel: 'Status',
      typeLabel: 'Client type',
      genderLabel: 'Gender',
      orgLabel: 'Organization',
      locationLabel: 'Location',
      riskLabel: 'Risk profile',
      referralLabel: 'Referral source',
      dobLabel: 'Date of birth',
      emergencyNameLabel: 'Emergency contact',
      emergencyPhoneLabel: 'Emergency phone',
      emergencyRelationLabel: 'Relation',
      emptyValue: '—',
      updatedToast: 'Profile updated.',
    },
    sensitive: {
      bannerLabel: 'Sensitive information',
      bannerDescription: 'Access to this section is audit-logged.',
      expandedDescription: 'You are viewing sensitive data. Your access is recorded.',
      expandCta: 'Expand',
      collapseCta: 'Collapse',
      editCta: 'Edit sensitive info',
      updatedToast: 'Sensitive info updated. Change is audit-logged.',
      drawerTitle: 'Edit sensitive information',
      drawerBannerLabel: 'You are editing sensitive information',
      drawerBannerDescription: 'Changes to this section are audit-logged.',
      loadErrorTitle: "Couldn't load sensitive info.",
    },
    edit: {
      pageTitle: 'Edit client',
      sensitiveHint: 'Sensitive info is edited from the client detail page.',
      basicSection: 'Basic',
      orgSection: 'Organization',
      riskSection: 'Risk and referral',
      typeSection: 'Client type',
    },
    redFlags: {
      emptyTitle: 'No red flags',
      emptyDescription: 'Flags raised during sessions appear here.',
      colDate: 'Date',
      colType: 'Type',
      colStatus: 'Status',
      colDescription: 'Description',
    },
    assessments: {
      emptyTitle: 'No assessments assigned',
      emptyDescription: 'Assigned assessments appear here once a practitioner sends them.',
      colTitle: 'Assessment',
      colAssigned: 'Assigned',
      colDue: 'Due',
      colStatus: 'Status',
    },
    // Aligned with the BE user_status enum (users.ts): the old INACTIVE and
    // SUSPENDED labels were collapsed into DISABLED server-side, and SHADOW
    // renders as "Dormant" (staff-created record awaiting the real client).
    statusMap: {
      activeLabel: 'Active',
      pendingInviteLabel: 'Pending invite',
      pendingVerificationLabel: 'Pending verification',
      disabledLabel: 'Disabled',
      dormantLabel: 'Dormant',
    },
    flagTypeMap: {
      crisis: 'High risk',
      acuteRisk: 'At risk',
    },
    flagStatusMap: {
      open: 'Open',
      underReview: 'Under review',
      closed: 'Closed',
    },
    assignmentStatusMap: {
      pending: 'Pending',
      inProgress: 'In progress',
      completed: 'Completed',
    },
    riskMap: {
      none: 'None',
      unknown: 'Unknown',
      veryLow: 'Very low',
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      veryHigh: 'Very high',
      critical: 'Critical',
    },
    typeMap: {
      student: 'Student',
      corporate: 'Corporate',
      staff: 'Staff',
      faculty: 'Faculty',
    },
  },

  organizations: {
    resource: 'organization',
    listTitle: 'Organizations',
    listDescription: {
      platform: 'Every organization across the network. Read-only oversight view.',
      workspace: 'Organizations in your business associate.',
    },
    newCta: 'New organization',
    detail: {
      tabOverview: 'Overview',
      tabLocations: 'Locations',
      tabUsers: 'Users',
      editCta: 'Edit',
      deleteCta: 'Delete',
    },
    overview: {
      sectionStats: 'Stats',
      sectionAbout: 'About',
      labelType: 'Type',
      labelCreated: 'Created',
      labelAddress: 'Address',
      labelCity: 'City',
      labelState: 'State',
      labelPincode: 'Pincode',
      labelCountry: 'Country',
      labelLocations: 'Locations',
      labelUsers: 'Users',
      emptyValue: '—',
    },
    locationsTab: {
      addCta: 'Add location',
      emptyTitle: 'No locations yet',
      emptyDescription: 'Add a location to start assigning staff and clients.',
      colName: 'Name',
      colCity: 'City',
      colPhone: 'Phone',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
    },
    usersTab: {
      addCta: 'Invite Org User',
      emptyTitle: 'No users in this organization',
      emptyDescription: 'Invite users from the Users page.',
      colName: 'Name',
      colEmail: 'Email',
      colRoles: 'Roles',
      colStatus: 'Status',
    },
    create: {
      pageTitle: 'New organization',
      sectionBasic: 'Basic',
      sectionAddress: 'Address',
      labelName: 'Name',
      labelType: 'Type',
      labelStreet: 'Street address',
      submitCta: 'Create organization',
      createdToast: 'Organization created.',
    },
    edit: {
      pageTitle: 'Edit organization',
      submitCta: 'Save changes',
      updatedToast: 'Organization updated.',
    },
    delete: {
      confirmTitle: 'Delete organization?',
      confirmDescription:
        'All locations will be soft-deleted and scheduled sessions will be cancelled. This cannot be undone.',
      confirmCta: 'Delete',
      successToast: 'Organization deleted.',
    },
    typeMap: {
      institution: 'Institution',
      corporate: 'Corporate',
    },
  },

  locations: {
    resource: 'location',
    detail: {
      pageTitle: 'Location',
      sectionDetails: 'Details',
      sectionServices: 'Services offered',
      sectionServicesHint:
        'Practitioner assignments at this location can claim a project only when the matching service is on.',
      labelPhone: 'Phone',
      labelAddress: 'Address',
      labelCity: 'City',
      labelState: 'State',
      labelPincode: 'Pincode',
      labelCountry: 'Country',
      labelOnCampusSupport: 'On-campus support',
      labelHelplineSupport: 'Helpline support',
      labelPortalHelpline: 'Portal helpline',
      labelChatSupport: 'Student chat',
      onCampusSupportHint: 'In-person and online sessions delivered at the institution.',
      helplineSupportHint: 'Phone-based support; sessions are backfilled afterwards.',
      portalHelplineHint: 'Instant on-demand video or voice calls from the client portal.',
      chatSupportHint: 'Text-based chat between clients and psychologists from the portal.',
      noServices: 'No services enabled — assignments at this location are blocked.',
      sectionAllowedDomains: 'Allowed email domains',
      sectionAllowedDomainsHint:
        'Clients self-registering with these email domains are routed to this location. Leave empty to allow any domain.',
      noAllowedDomains: 'Any email domain accepted.',
      editCta: 'Edit',
      emptyValue: '—',
    },
    create: {
      pageTitle: 'New location',
      sectionBasic: 'Basic',
      sectionAddress: 'Address',
      sectionServices: 'Services',
      sectionServicesDescription:
        'Pick which projects this location can host. Practitioner assignments here can only claim the matching project.',
      sectionAllowedDomains: 'Allowed email domains',
      sectionAllowedDomainsDescription:
        'Optional. Restrict client self-registration at this location to these email domains (e.g., "vit.ac.in"). Add domains by typing and pressing Enter.',
      labelName: 'Name',
      labelStreet: 'Street address',
      labelAllowedDomains: 'Allowed email domains',
      allowedDomainsPlaceholder: 'e.g. vit.ac.in',
      submitCta: 'Create location',
      createdToast: 'Location added.',
    },
    edit: {
      pageTitle: 'Edit location',
      submitCta: 'Save changes',
      updatedToast: 'Location updated.',
    },
    delete: {
      confirmTitle: 'Delete location?',
      confirmDescription: 'This cannot be undone.',
      confirmCta: 'Delete',
      successToast: 'Location deleted.',
    },
  },

  sessions: {
    resource: 'session',
    listTitle: 'Sessions',
    listDescription: {
      platform: 'Every session across the network. Read-only monitoring view.',
      workspace: 'Sessions in your business associate. Read-only.',
    },
    detail: {
      titlePrefix: 'Session',
      tabOverview: 'Overview',
      tabNotes: 'Notes',
      tabCaseReports: 'Case reports',
      tabFeedback: 'Feedback',
    },
    overview: {
      sectionScheduling: 'Scheduling',
      sectionParticipants: 'Participants',
      sectionConcern: 'Concern',
      labelStatus: 'Status',
      labelType: 'Type',
      labelMode: 'Mode',
      labelScheduledStart: 'Scheduled start',
      labelScheduledEnd: 'Scheduled end',
      labelActualStart: 'Actual start',
      labelActualEnd: 'Actual end',
      labelDuration: 'Duration',
      labelClient: 'Client',
      labelPractitioner: 'Practitioner',
      labelOrganization: 'Organization',
      labelLocation: 'Location',
      labelBookedBy: 'Booked by',
      emptyValue: '—',
      noConcern: 'No concern recorded.',
    },
    notes: {
      emptyTitle: 'No session note yet',
      emptyDescription: 'The practitioner has not recorded a note for this session.',
      forbiddenTitle: 'Restricted',
      forbiddenDescription:
        'Session notes are clinical content. Only chief, lead, and treating psychologists can view them.',
      draftLabel: 'Draft',
      submittedLabel: 'Submitted',
    },
    caseReports: {
      emptyTitle: 'No case reports',
      emptyDescription: 'Case reports submitted for this session will appear here.',
      colCreated: 'Created',
      colStatus: 'Status',
      colSummary: 'Summary',
      forbiddenTitle: 'Restricted',
      forbiddenDescription: 'You do not have permission to view case reports.',
    },
    feedback: {
      emptyTitle: 'No feedback yet',
      emptyDescription: 'Client feedback for this session will appear here once submitted.',
      labelOverall: 'Overall',
      labelHeard: 'Felt heard',
      labelClarity: 'Clarity',
      labelComfort: 'Comfort',
      labelComment: 'Comment',
      labelSubmittedAt: 'Submitted',
      forbiddenTitle: 'Restricted',
      forbiddenDescription: 'You do not have permission to view feedback.',
    },
    filters: {
      labelDate: 'Date range',
      labelStatus: 'Status',
      labelType: 'Type',
      labelMode: 'Mode',
      labelProject: 'Project',
      labelCategory: 'Call category',
      labelDirection: 'Call direction',
      labelRedFlags: 'Red flags',
      labelOrganization: 'Organization',
      labelLocation: 'Location',
      labelPractitioner: 'Practitioner',
      labelClient: 'Client',
      practitionerHint: 'Type a name or email to find a practitioner.',
      clientHint: 'Type a name or email to find a client.',
      organizationHint: 'Type to find an organization.',
      locationHint: 'Pick an organization first, then a location.',
    },
    statusMap: {
      scheduled: 'Scheduled',
      in_progress: 'In progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
      no_show: 'No show',
      rescheduled: 'Rescheduled',
    },
    typeMap: {
      new: 'New',
      follow_up: 'Follow-up',
      crisis: 'Crisis',
    },
    modeMap: {
      online: 'Online',
      in_person: 'In person',
      helpline: 'Helpline',
    },
    noteStatusMap: {
      draft: 'Draft',
      // Sealed > Submitted — the act of finalising a clinical note is more
      // an "I'm closing this chapter" gesture than a form submission, and
      // the journal-page chrome leans into that framing.
      submitted: 'Sealed',
    },
    caseReportStatusMap: {
      draft: 'Draft',
      // "In review" reads more honestly than "Submitted" — the chief
      // psychologist actually reviews the report; the practitioner has
      // handed it over.
      submitted: 'In review',
      approved: 'Approved',
      rejected: 'Sent back',
    },
  },

  redFlags: {
    listTitle: 'Red flags',
    listDescription: {
      platform: 'Every red flag raised across the network. Read-only oversight view.',
      workspace: 'Red flags raised across your business associate. Read-only.',
    },
    colCreated: 'Raised',
    colType: 'Type',
    colStatus: 'Status',
    colClient: 'Client',
    colSession: 'Session',
    colRaisedBy: 'Raised by',
    colDescription: 'Description',
    expand: 'Expand',
    collapse: 'Collapse',
    viewClient: 'View client',
    viewSession: 'View session',
    sessionShortLabel: 'Session',
    noDescription: 'No description recorded.',
    actionTakenLabel: 'Action taken',
    assignedToLabel: 'Assigned to',
    resolvedAtLabel: 'Resolved',
    resolvedByLabel: 'Resolved by',
    emptyTitle: 'No red flags recorded',
    emptyDescription: 'No flags raised yet — the absence of flags is a good sign.',
    noResultsTitle: 'No red flags match',
    noResultsDescription: 'Try clearing the type filter to see more flags.',
    filters: {
      labelType: 'Type',
    },
  },
} as const;

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Resolve a template string with `{resource}`, `{Resource}`, `{resources}`,
 * `{Resources}`, `{n}`, `{parent}`, and `{email}` placeholders.
 */
export function formatResourceCopy(
  template: string,
  resource: string,
  opts?: { count?: number; parent?: string; email?: string },
): string {
  const plural = resource.endsWith('s') ? resource : `${resource}s`;
  return template
    .replaceAll('{Resource}', capitalize(resource))
    .replaceAll('{resource}', resource)
    .replaceAll('{Resources}', capitalize(plural))
    .replaceAll('{resources}', plural)
    .replaceAll('{n}', opts?.count !== undefined ? String(opts.count) : '{n}')
    .replaceAll('{parent}', opts?.parent ?? '{parent}')
    .replaceAll('{email}', opts?.email ?? '{email}');
}

/**
 * Canonical user-facing strings for the clinical (Psychologist-flow) surfaces.
 * Follows the same voice rules as adminCopy: direct, professional, warm.
 * No exclamation marks; sentence case; active voice.
 */
export const clinicalCopy = {
  toast: {
    reportSubmitted: 'Case report submitted for review.',
    reportApproved: 'Case report approved.',
    reportRejected: 'Case report returned to the author.',
    noteSubmitted: 'Session note submitted.',
    flagRaised: 'Red flag raised. Chief psychologists have been notified.',
    phiRateLimited:
      "You've viewed sensitive data several times in the last minute. Try again shortly.",
    flagRaiseRateLimited: 'Too many flags raised in the last minute. Try again shortly.',
    approvalRace: 'This report has already been actioned. Refreshing now.',
    slotTaken: 'That time slot was just booked. Showing updated availability.',
    recordingStartFailed: "Recording couldn't start. The call continues.",
    recordingStopFailed: "Recording couldn't stop. The call continues.",
    participantActionFailed: "Couldn't complete that action.",
    callEndFailed: "Couldn't end the call on the server. Disconnecting you locally.",
    wrapUpConcernsNotSaved:
      'The session was already completed. Your concerns were not saved. Retag them from the session page.',
    wrapUpRiskNotSaved:
      'The session was already completed. The risk profile was not saved. Update it from the client page.',
    cameraDeviceError: "Couldn't connect your camera. The call is continuing.",
    microphoneDeviceError: "Couldn't connect your microphone. The call is continuing.",
    saveFailed: "Couldn't save. We'll keep retrying.",
    noteSubmitFailed: "Couldn't submit the note. Please try again.",
    reportSubmitFailed: "Couldn't submit the report. Please try again.",
    approveFailed: "Couldn't approve the report. Please refresh and try again.",
    rejectFailed: "Couldn't reject the report. Please refresh and try again.",
    exerciseAssigned: 'Exercise assigned.',
    exerciseReviewed: 'Review saved. The client will see it in their self-help feed.',
    exerciseAssignFailed: "Couldn't assign the exercise. Please try again.",
    exerciseReviewFailed: "Couldn't save the review. Please refresh and try again.",
  },
  phi: {
    auditNotice: 'Viewing this data will be recorded in the audit log.',
    showButtonPrefix: 'Show', // used as `${showButtonPrefix} ${category}`
    hide: 'Hide',
  },
} as const;
